import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Plus } from '@phosphor-icons/react';
import { Button } from 'antd';
import { BodyContainer } from '../../../../../elements/Common';
import { RouteTabs } from '../../../../../components';
import { CacheTimes, CacheUtility, CommonUtility, Tabs } from '../../../../../utility';
import { WaveListTab } from './list';
import { SortedWaveListTab } from './sortedLists';
import { UnAssignedListTab } from './unassigned-list';
import { WaveSearchInput } from '../../../../../page-components/waves/WaveSearch';
import { WavesTabToolbar } from '../../../../../page-components/waves/waves-tab-toolbar';
import { MobileWaveList } from './mobile-list';
import { CreateWavePopup } from '../../../../../page-components';
import WaveSummaryBar from '../../../../../page-components/waves/WaveSummaryBar';
import { AnalyticsTab } from './analytics-list';
import { useGlobalContext } from '../../../../../contexts';
import {
  useWavesTabExtraContent,
  WavesTabExtraContext,
} from '../../../../../contexts/waves-tab-extra';
import { useSecondaryHeader } from '../../../../../hooks/useSecondaryHeader';
import { WaveContext } from './waves-list-context';
import { useTabletAndBelowMediaQuery } from '../../../../../layouts/ResponsiveMedia';

function WavesSecondaryHeader({ menus, currentTab, tabClicked }) {
  const { tabExtra } = useContext(WavesTabExtraContext);

  return (
    <RouteTabs
      items={menus}
      activeKey={currentTab}
      onChange={tabClicked}
      tabBarExtraContent={tabExtra}
      className="no-header"
    />
  );
}

export function WaveListScreen() {
  const cacheKey = 'wave-list';
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { warehouseId, changeWarehouse } = useGlobalContext();

  const [search, setSearch] = useState('');
  const [createWavePopup, setCreateWavePopup] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [statuses, setStatuses] = useState();

  const [analyticsParams, setAnalyticsParams] = useState();
  const [waveBulkActions, setWaveBulkActions] = useState(null);

  const handleWaveSearchSelect = item => {
    navigate(`/app/fulfill/waves/${item._id}`);
  };

  const createWave = () => setCreateWavePopup(true);

  const currentTab = useMemo(() => {
    if (pathname) {
      return CommonUtility.getSegmentByPosition(pathname, 5);
    }
    return null;
  }, [pathname]);

  const tabClicked = tabName => {
    navigate(`/app/fulfill/waves/list/${tabName}`);
    CacheUtility.save(cacheKey, { tabName }, CacheTimes.TWO_HOURS);
  };

  useEffect(() => {
    const cache = CacheUtility.get(cacheKey);
    if (cache?.tabName) {
      tabClicked(cache.tabName);
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: restore tab once on mount
  }, []);

  const menus = [
    {
      text: 'Building',
      value: Tabs.WavesTabs.building,
    },
    {
      text: 'Ready',
      value: Tabs.WavesTabs.ready,
    },
    {
      text: 'In Progress',
      value: Tabs.WavesTabs.in_progress,
    },
    {
      text: 'Sorting',
      value: Tabs.WavesTabs.sorting,
    },
    {
      text: 'Sorted',
      value: Tabs.WavesTabs.sorted,
    },
    {
      text: 'All',
      value: Tabs.WavesTabs.all,
    },
    {
      text: 'Unassigned',
      value: Tabs.WavesTabs.unassigned,
    },
    {
      text: 'Analytics',
      value: Tabs.WavesTabs.analytics,
    },
  ];

  const isWaveTab = ![Tabs.WavesTabs.unassigned, Tabs.WavesTabs.sorted].includes(
    currentTab,
  );

  const isTabletAndBelow = useTabletAndBelowMediaQuery();
  const refresh = () => setRefreshKey(key => key + 1);

  useSecondaryHeader(
    () => (
      <WavesSecondaryHeader
        menus={menus}
        currentTab={currentTab}
        tabClicked={tabClicked}
      />
    ),
    [currentTab, tabClicked],
  );

  const showWaveListToolbar = isWaveTab && currentTab !== Tabs.WavesTabs.analytics;

  useWavesTabExtraContent(
    () => (
      <WavesTabToolbar>
        {waveBulkActions}
        <Button
          type="primary"
          icon={<Plus size={16} weight="bold" />}
          onClick={createWave}
        >
          Wave
        </Button>
        <WaveSearchInput
          onResultClick={handleWaveSearchSelect}
          setTableSearch={setSearch}
          warehouseId={warehouseId}
        />
      </WavesTabToolbar>
    ),
    [warehouseId, createWave, handleWaveSearchSelect, waveBulkActions],
    showWaveListToolbar,
  );

  return (
    <WaveContext.Provider
      value={{
        setWarehouseId: changeWarehouse,
        warehouseId,
        search,
        setSearch,
        createWave,
        onWaveSearchSelect: handleWaveSearchSelect,
        setWaveBulkActions,
      }}
    >
      <BodyContainer className="tab-layout no-header">
        <WaveSummaryBar
          warehouseId={warehouseId}
          status={statuses}
          analyticsParams={analyticsParams}
        />

        {isTabletAndBelow ? (
          <MobileWaveList setTableSearch={setSearch} />
        ) : (
          <Routes>
            <Route path="sorted/*" element={<SortedWaveListTab />} />
            <Route path="unassigned" element={<UnAssignedListTab />} />
            <Route
              path="analytics"
              element={
                <AnalyticsTab
                  warehouseId={warehouseId}
                  search={search}
                  setAnalyticsParams={setAnalyticsParams}
                />
              }
            />
            <Route
              path="*"
              element={
                <WaveListTab
                  refreshKey={refreshKey}
                  setStatuses={setStatuses}
                  refresh={refresh}
                />
              }
            />
          </Routes>
        )}
        <CreateWavePopup
          open={createWavePopup}
          onClose={() => setCreateWavePopup(false)}
          warehouseId={warehouseId}
          refresh={refresh}
        />
      </BodyContainer>
    </WaveContext.Provider>
  );
}

export { useWavesFilterContext } from './waves-list-context';
